import { Injectable } from '@angular/core';
import { AllowedCurrencies, ContractTemplate, Garage, GarageContract, Money, NextPrice } from '@shared/models';
import { map, Observable, of, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { ToastService } from '@shared/services';
import { TranslateService } from '@ngx-translate/core';

export interface ContractTemplateInfo {
  discount: {
    type: string;
    value: Money;
  } | null;
  price: Money;
  vat_percent: number;
  earliest_termination_date: string;
  first_payment_period: {
    price: Money;
    start: string;
    end: string;
  };
  increased_price?: Money  | null;
  next_price: NextPrice | null;
  next_price_within_commitment: boolean;
}

interface ContractTemplateInfoResponse {
  name: string;
  group_limit: number;
  start: string;
  end: string | null;
  discount_type: string;
  discount_amount: string;
  price: string;
  price_currency: AllowedCurrencies;
  vat_percent: string;
  first_termination_date: string;
  first_payment_period: {
    price: string;
    start: string;
    end: string;
  };
  adjusted_price: string | null;
  next_price: {
    amount: string;
    currency: AllowedCurrencies;
    valid_from: string;
  } | null;
  next_price_within_commitment: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ContractCreateService {
  garageSubject: Subject<Garage> = new Subject<Garage>();
  productSubject: Subject<ContractTemplate> = new Subject<ContractTemplate>();

  constructor(
    private http: HttpClient,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {}

  getContractTemplateInfo(
    id: ContractTemplate['id'],
    group_limit: GarageContract['group_limit'],
    start: string,
  ): Observable<ContractTemplateInfo> {
    return this.http
      .get<ContractTemplateInfoResponse>(
        `/api/backoffice/v2/garage_contract/preview/?contract_template_id=${id}&group_limit=${group_limit}&start=${start}`,
      )
      .pipe(
        map((x) => this.deserializeContractTemplateInfo(x)),
        catchError(() => {
          this.toastService.add({ type: 'error', message: this.translateService.instant('Common.Errors.PageCouldNotBeLoaded') });
          return of();
        }),
      );
  }

  deserializeContractTemplateInfo(response: ContractTemplateInfoResponse): ContractTemplateInfo {
    return {
      discount: response.discount_type
        ? { type: response.discount_type, value: { amount: parseFloat(response.discount_amount), currency: response.price_currency } }
        : null,
      price: { amount: parseFloat(response.price), currency: response.price_currency },
      vat_percent: parseFloat(response.vat_percent),
      earliest_termination_date: response.first_termination_date,
      first_payment_period: {
        price: { amount: parseFloat(response.first_payment_period.price), currency: response.price_currency },
        start: response.first_payment_period.start,
        end: response.first_payment_period.end,
      },
      increased_price: response.adjusted_price ? { amount: parseFloat(response.adjusted_price), currency: response.price_currency } : null,
      next_price: response.next_price ? { ...response.next_price, amount: parseFloat(response.next_price.amount) } : null,
      next_price_within_commitment: response.next_price_within_commitment ?? false,
    };
  }
}
